import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ProdutoDoCatalogo } from "../lib/adminCatalogo";
import type { PropsDoCatalogo } from "./AdminCatalogo";
import AdminCatalogoProduto from "./AdminCatalogoProduto";

// Admin › Catálogo › Produtos — os 12 SKUs em 11 cursos, com a CONFERÊNCIA dos
// sete lugares em que um produto vive (000253-000256).
//
// ---------------------------------------------------------------------------
// Por que a lista é agrupada por CURSO, e não uma lista de 12 produtos
// ---------------------------------------------------------------------------
// "O curso do aluno não é o produto do catálogo" é regra desta casa, e a Escola
// é a prova viva: `escola-contabil` e `escola-vitalicio` são dois SKUs (é assim
// que a Guru vende) e UM curso na tela — 29.882 e 5.163 alunos, o mesmo
// `curso_slug`, os mesmos 10 cards. Uma lista chapada de 12 linhas esconderia
// exatamente isso, e quem fosse mexer no prazo da Escola mexeria em um dos dois
// sem saber que o outro existe.
//
// Então o agrupamento não é organização: é a regra desenhada. Onde um curso tem
// mais de um produto, a tela DIZ o que isso significa.
//
// ---------------------------------------------------------------------------
// A CONFERÊNCIA é a peça central, e ela é derivada no BANCO
// ---------------------------------------------------------------------------
// `produto.falta` chega pronto, com os sete lugares e a frase do que acontece
// com o aluno quando cada um está vazio. Esta tela não recalcula nenhum deles —
// uma segunda definição de "este produto está completo?" escrita aqui
// divergiria da primeira em silêncio, que é o defeito de "agregar acesso em
// JavaScript" com outra roupa.
//
// A frase (`diz`) aparece À VISTA, nunca atrás de um tooltip. "falta memberkit"
// não informa nada a quem não viveu a cicatriz; "sem nível de assinatura no
// Memberkit — a porta Aulas não abre" informa. Quem lê esta tela está decidindo
// se mexe em produção, e o que ele precisa ler é a consequência.
//
// E o inverso: produto COMPLETO não ganha selo verde. Medido em 16/09, os 12
// produtos estão com `falta` vazio — doze selos "completo" seriam doze linhas
// de ruído dizendo que está tudo bem, que é a definição de zero grande. Quem
// precisa do total lê a única linha do topo.
//
// ---------------------------------------------------------------------------
// Esta tela não EDITA mais nada (21/09)
// ---------------------------------------------------------------------------
// O formulário dos 11 campos do SKU morava aqui e saiu, a pedido de Victor:
// *"a tela de produtos deve ter uma tela de edição dedicada assim como nas
// ferramentas, quando clicamos no botão Abrir"*. Ele agora é
// `AdminCatalogoProdutoEditar`, dentro da ficha do produto.
//
// A REMOÇÃO é a metade que importa do pedido, e é a mesma decisão que a
// ferramenta tomou em 16/09: duas telas editando o mesmo produto seriam duas
// portas para a mesma ação, e a segunda é a que fica desatualizada. Daqui o
// botão "Editar" LEVA à ficha com o SKU já escolhido — ele não abre nada no
// lugar.
//
// O que esta tela continua não fazendo:
//   · **não cria produto** — ele vive em sete lugares, e criar só o primeiro faz
//     o aluno pagar e não ver o curso. A recusa do banco diz isso com a mesma
//     frase, então aqui a nota é uma linha e não um alarme.
//   · **não edita as variantes de contrato** — a variante é da OFERTA, e quem a
//     escolhe é quem decide a venda de cada id externo. Aqui elas são leitura.
//
// ---------------------------------------------------------------------------
// Não há busca, de propósito
// ---------------------------------------------------------------------------
// São 12 produtos em 11 grupos: a lista inteira cabe em pouco mais de uma tela.
// Um campo de busca aqui seria um controle para procurar dentro do que já está
// todo visível — e o filtro "só os com pendência", hoje, esvaziaria a tela,
// porque não há nenhuma.

/** O rótulo de cada tipo. Valor desconhecido cai no CRU, nunca numa frase errada. */
const ROTULO_DO_TIPO: Record<string, string> = {
  curso: "curso",
  pos: "pós",
  mba: "MBA",
  formacao: "formação",
};

/**
 * `2033-12-31` → `31/12/2033`, por SPLIT.
 *
 * Não use `dataCurta` da lib para ISTO: ela faz `new Date(iso)`, e uma data SEM
 * hora é lida como meia-noite em UTC — que em -03:00 é o DIA ANTERIOR. O prazo
 * da Pós RT (`2033-12-31`) apareceria na tela como 30/12/2033, um dia a menos,
 * sem nenhum erro. É a mesma cicatriz do fato gerador ao meio-dia das
 * Calculadoras. `dataCurta` continua certa para o que ela recebe: carimbo com
 * hora.
 */
function dataFixaBr(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
}

/**
 * O prazo em linguagem de gente.
 *
 * "1 meses" é a cicatriz do "cerca de 1 páginas" do Leitor: concordância num
 * texto que fala de número é peça de código, não de redação.
 *
 * E "vitalício" é DATA NULA, nunca data grande — o vocabulário da casa. Por
 * isso o ramo não tem número nenhum para mostrar.
 */
function prazoEmPalavras(p: ProdutoDoCatalogo): string {
  if (p.prazo_tipo === "vitalicio") return "vitalício";
  if (p.prazo_tipo === "data_fixa") {
    return p.prazo_data_fixa ? `até ${dataFixaBr(p.prazo_data_fixa)}` : "data fixa sem data";
  }
  if (!p.prazo_meses) return "prazo em meses sem número";
  return `${p.prazo_meses} ${p.prazo_meses === 1 ? "mês" : "meses"}`;
}

type Grupo = {
  curso_slug: string;
  nome: string;
  faculdade: boolean;
  produtos: ProdutoDoCatalogo[];
};

export default function AdminCatalogoProdutos({ catalogo, recarregar, veFeedbacks = false }: PropsDoCatalogo) {
  // O produto ABERTO vive na QUERY, e não em `useState`: o operador manda o
  // link da pagina de um curso a um colega, volta pelo botao do navegador, e
  // recarregar nao o joga de volta para a lista. E a mesma razao de a sub-aba
  // ser sub-rota.
  const [params] = useSearchParams();
  const produtoAberto = params.get("produto") ?? "";
  // 30 produtos desenhados de uma vez. Hoje o botão NUNCA aparece — são 12 —, e
  // ele existe porque o catálogo cresce: a Pós Auditoria entrou em 09/09 e foi o
  // primeiro produto novo depois da carga. O teto vem da régua da casa, que é a
  // mesma da fila do admin (41.910px com 200 itens) e da busca de CNAE.
  const [mostrando, setMostrando] = useState(30);

  // Os grupos saem dos PRODUTOS, e o nome vem da lista de cursos com o
  // `curso_nome` do próprio produto como reserva. A ordem importa: partir de
  // `catalogo.cursos` faria sumir da tela qualquer produto cujo curso não
  // estivesse lá — e produto que some de uma tela de conferência é o defeito que
  // esta tela existe para pegar.
  const grupos = useMemo<Grupo[]>(() => {
    const porCurso = new Map<string, Grupo>();
    for (const p of catalogo.produtos) {
      const existente = porCurso.get(p.curso_slug);
      if (existente) {
        existente.produtos.push(p);
        continue;
      }
      const curso = catalogo.cursos.find((c) => c.slug === p.curso_slug);
      porCurso.set(p.curso_slug, {
        curso_slug: p.curso_slug,
        nome: curso?.nome ?? p.curso_nome,
        faculdade: curso?.faculdade ?? false,
        produtos: [p],
      });
    }
    // Desempate por `curso_slug`, que é único: ordenar só pelo nome deixaria
    // dois cursos homônimos trocando de lugar entre renders, e ordenação por
    // chave que empata é a cicatriz do desempate por uuid.
    const lista = [...porCurso.values()].sort(
      (a, b) => a.nome.localeCompare(b.nome, "pt-BR") || a.curso_slug.localeCompare(b.curso_slug),
    );
    for (const g of lista) g.produtos.sort((a, b) => a.slug.localeCompare(b.slug));
    return lista;
  }, [catalogo.produtos, catalogo.cursos]);

  // Quantos produtos têm pendência. Sai da MESMA lista que a tela desenha: um
  // número contado de uma fonte e desenhado de outra é quem conta mentindo sobre
  // quem mostra.
  const comPendencia = useMemo(
    () => catalogo.produtos.filter((p) => p.falta.length > 0).length,
    [catalogo.produtos],
  );

  // O corte respeita os grupos: um curso não é partido no meio por causa do
  // orçamento, porque metade da Escola na tela seria pior que a página inteira.
  //
  // Por isso `mostrando` é TETO MOLE: o último grupo entra INTEIRO e pode passar
  // dele. A versão anterior deste bloco cortava o grupo que não coubesse
  // (`produtos.slice(0, orcamento)`) com este mesmo comentário em cima — código
  // que contradiz a decisão escrita ao lado é a cicatriz da 000131, e quem
  // pagaria por ela era a Escola, o único curso com mais de um SKU.
  const { visiveis, restantes } = useMemo(() => {
    const saida: Grupo[] = [];
    let orcamento = mostrando;
    for (const g of grupos) {
      if (orcamento <= 0) break;
      saida.push(g);
      orcamento -= g.produtos.length;
    }
    const desenhados = saida.reduce((n, g) => n + g.produtos.length, 0);
    return { visiveis: saida, restantes: catalogo.produtos.length - desenhados };
  }, [grupos, mostrando, catalogo.produtos.length]);

  // A página DEDICADA do produto (000262). Vem DEPOIS de todos os hooks: um
  // `return` no meio deles derruba a rota inteira com o erro React #310, e nem
  // o `tsc` nem a suíte SQL veem isso — o hub não tem eslint.
  if (produtoAberto) {
    return (
      <AdminCatalogoProduto
        catalogo={catalogo}
        curso={produtoAberto}
        voltar="/admin/catalogo/produtos"
        recarregar={recarregar}
        veFeedbacks={veFeedbacks}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* ── O resumo ───────────────────────────────────────────────────────── */}
      <div className="surface rise p-5 sm:p-6">
        <h2 className="font-display text-lg font-bold text-titulo">Produtos do catálogo</h2>
        <p className="mt-1 text-sm text-cinza">
          {catalogo.produtos.length} produto(s) em {grupos.length} curso(s)
          {/* Os dois ramos são fatos, e o do zero é o mais importante: sem ele o
              operador não sabe se a conferência rodou e não achou nada ou se ela
              simplesmente não aparece nesta tela. */}
          {comPendencia > 0
            ? ` · ${comPendencia} com pendência de cadastro`
            : " · nenhum com pendência de cadastro"}
        </p>
        <p className="mt-3 text-sm text-cinza">
          Esta tela não cria produto: ele vive em sete lugares, e criar só o primeiro faz o aluno
          pagar e não ver o curso. Produto novo entra por migration, com os sete conferidos.
        </p>
      </div>

      {/* ── A lista, por curso ─────────────────────────────────────────────── */}
      {/* Um cartão por CURSO, e não uma fita única de 13 linhas.

          Pedido de Victor em 16/09, olhando a tela: "desgrude um pouco um
          produto do outro". A separação segue a unidade que o produto tem —
          o CURSO —, porque é ela que o aluno vê: a Escola tem dois SKUs e uma
          tela só, e os dois precisam continuar juntos dentro do mesmo cartão
          enquanto se afastam dos outros dez cursos. Separar produto a produto
          desfaria justamente o agrupamento que o cabeçalho existe para
          explicar. */}
      <ul className="space-y-4">
          {visiveis.map((g) => (
            <li
              key={g.curso_slug}
              data-curso={g.curso_slug}
              className="surface rise overflow-hidden"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 bg-chip/40 px-5 py-3">
                <div className="min-w-60 flex-1">
                <h3 className="text-sm font-bold text-titulo">
                  {g.nome}
                  <span className="ml-2 font-mono text-xs font-normal text-cinza">
                    {g.curso_slug}
                  </span>
                  {g.faculdade && (
                    <span className="ml-2 rounded-full bg-chip px-2 py-0.5 text-xs font-normal text-chip-tinta">
                      faculdade
                    </span>
                  )}
                </h3>
                {/* Só onde o curso tem mais de um SKU — que hoje é a Escola, e é
                    a regra desta casa em estado bruto. Dizer isto nos 10 grupos
                    de um produto só seria a repetição que a régua manda contar
                    antes de dar a tela por pronta. */}
                {g.produtos.length > 1 && (
                  <p data-varios-skus className="mt-1 text-xs text-cinza">
                    {g.produtos.length} produtos (SKUs) e UM curso na tela do aluno — é assim que a
                    Guru vende. Quem agrupa é o <code>curso_slug</code>; a vigência do conjunto é
                    derivada, e mexer em um SKU não mexe no outro.
                  </p>
                )}
                </div>
                {/* Um LINK, e não um botão: a página do produto tem endereço
                    próprio, e o operador precisa poder mandá-lo a um colega e
                    voltar pelo botão do navegador. */}
                <Link
                  to={`/admin/catalogo/produtos?produto=${encodeURIComponent(g.curso_slug)}`}
                  data-abrir-produto={g.curso_slug}
                  className="btn-contorno inline-flex min-h-9 shrink-0 items-center self-center text-xs"
                >
                  Abrir a página
                </Link>
              </div>

              <ul className="divide-y divide-borda">
                {g.produtos.map((p) => (
                  <li
                    key={p.slug}
                    data-produto={p.slug}
                    className={`flex flex-wrap items-start gap-4 px-5 py-5 ${
                      p.ativo ? "" : "bg-chip/40"
                    }`}
                  >
                    <div className="min-w-56 flex-1">
                      <p className="font-semibold text-titulo">
                        {p.nome}
                        <span className="ml-2 font-mono text-xs font-normal text-cinza">
                          {p.slug}
                        </span>
                        {!p.ativo && (
                          <span className="ml-2 rounded-full bg-alerta-fundo px-2 py-0.5 text-xs font-normal text-alerta-tinta">
                            desativado
                          </span>
                        )}
                      </p>

                      <p className="mt-0.5 text-sm text-cinza">
                        {ROTULO_DO_TIPO[p.tipo] ?? p.tipo} · {prazoEmPalavras(p)} ·{" "}
                        {p.alunos.toLocaleString("pt-BR")} aluno(s) com acesso vigente · {p.ids}{" "}
                        id(s) · {p.cards} card(s)
                        {p.bonus.length > 0 && ` · ${p.bonus.length} bônus`}
                      </p>

                      {/* O SKU que chama o curso de outro nome. Só aparece na
                          divergência, e ela é alcançável DESTA tela: o nome do
                          curso é campo do produto, não do curso. */}
                      {p.curso_nome !== g.nome && (
                        <p data-curso-nome-diverge className="mt-1 text-xs text-alerta-tinta">
                          Este SKU chama o curso de "{p.curso_nome}".
                        </p>
                      )}

                      {/* ── A CONFERÊNCIA ─────────────────────────────────── */}
                      {p.falta.length > 0 && (
                        <ul className="mt-2 flex flex-col gap-1 rounded-cf-lg border border-alerta-tinta/40 bg-alerta-fundo p-3">
                          {p.falta.map((f) => (
                            <li
                              key={f.lugar}
                              data-falta={f.lugar}
                              className="text-sm text-alerta-tinta"
                            >
                              <strong className="font-mono text-xs">{f.lugar}</strong> — {f.diz}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* ── As variantes de contrato (leitura) ────────────── */}
                      {p.variantes.length > 0 && (
                        <div className="mt-2">
                          <ul className="flex flex-col gap-0.5">
                            {p.variantes.map((v) => (
                              <li key={v.variante} data-variante={v.variante} className="text-sm text-cinza">
                                contrato <strong className="text-tinta">{v.variante}</strong> ·{" "}
                                {v.ofertas} oferta(s){" "}
                                {v.contrato_url && (
                                  <a
                                    href={v.contrato_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-link underline"
                                  >
                                    ver documento
                                    <span className="sr-only"> (abre em outra aba)</span>
                                  </a>
                                )}
                              </li>
                            ))}
                          </ul>
                          {/* A sub-aba Vendas saiu em 21/09. A frase que
                              mandava o operador para lá virou esta: o fato
                              continua valendo (a variante é escolhida ao
                              decidir a venda), e o lugar que não existe mais
                              deixou de ser citado. */}
                          <p className="mt-1 text-xs text-cinza">
                            O contrato por oferta não se edita aqui: a variante é escolhida quando
                            se decide a venda de cada id externo.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* A edição mora na ficha do produto desde 21/09, e daqui
                        se vai até ela com o SKU já escolhido. É LINK, e não
                        botão, porque muda de página: o operador manda o
                        endereço a um colega e volta pelo navegador. */}
                    <Link
                      to={`/admin/catalogo/produtos?produto=${encodeURIComponent(
                        p.curso_slug,
                      )}&sku=${encodeURIComponent(p.slug)}`}
                      data-editar-produto={p.slug}
                      className="btn-contorno inline-flex min-h-10 items-center text-sm"
                    >
                      Editar
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
      </ul>

      {restantes > 0 && (
        <div className="text-center">
          <button
            type="button"
            data-mostrar-mais
            onClick={() => setMostrando((n) => n + 30)}
            className="btn-contorno min-h-10 text-sm"
          >
            Mostrar mais ({restantes} restantes)
          </button>
        </div>
      )}
    </div>
  );
}
